/* Shared marketing atoms — used by Landing, Pricing, ContactSales, Demo, and
 * static Legal/Security pages. Theme-independent (hard-coded palette) so the
 * marketing surface renders identically in light or dark mode.
 */
import { Link, useLocation } from "react-router-dom";
import { useState } from "react";

export const RED = "#DC2626";
export const RED_DARK = "#B91C1C";
export const INK = "#0F172A";
export const MUTED = "#64748B";
export const CREAM = "#FAFAF7";
export const BORDER = "#E5E7EB";

export const ZAROORI_JINI_LOGO_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_scalable-fashion-oms/artifacts/es403apk_zaroori%20jini%20oms.png";

/* ---------- primitives ---------- */
export function Chip({ children }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 14px", border: `1px solid ${RED}33`,
        color: RED, background: "#FFF5F5", fontSize: 12, fontWeight: 600,
        letterSpacing: "0.08em", textTransform: "uppercase", borderRadius: 999,
      }}
    >
      {children}
    </span>
  );
}

export function Section({ id, children, style }) {
  return (
    <section id={id} style={{ padding: "80px 24px", ...style }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>{children}</div>
    </section>
  );
}

export function SectionTitle({ eyebrow, title, description, center = true }) {
  return (
    <div style={{ textAlign: center ? "center" : "left", marginBottom: 48 }}>
      {eyebrow && (
        <div
          style={{
            color: RED, fontWeight: 700, letterSpacing: "0.14em",
            fontSize: 12, textTransform: "uppercase", marginBottom: 12,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h2
        style={{
          fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, color: INK,
          lineHeight: 1.1, letterSpacing: "-0.02em",
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            color: MUTED, fontSize: 17, maxWidth: 720,
            margin: center ? "16px auto 0" : "16px 0 0", lineHeight: 1.6,
          }}
        >
          {description}
        </p>
      )}
    </div>
  );
}

export function PrimaryButton({ children, onClick, type = "button", full = false, disabled = false, testid, as, to, href }) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "13px 22px", background: disabled ? "#F1F1F1" : RED, color: disabled ? "#999" : "#fff",
    fontWeight: 600, fontSize: 14, borderRadius: 8, border: "none",
    cursor: disabled ? "not-allowed" : "pointer", width: full ? "100%" : "auto",
    transition: "background 120ms ease, transform 120ms ease",
    boxShadow: disabled ? "none" : "0 6px 20px -8px rgba(220,38,38,0.5)",
    textDecoration: "none",
  };
  const hoverIn = (e) => { if (!disabled) e.currentTarget.style.background = RED_DARK; };
  const hoverOut = (e) => { if (!disabled) e.currentTarget.style.background = RED; };
  if (as === "link" && to) {
    return (
      <Link to={to} data-testid={testid} style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        {children}
      </Link>
    );
  }
  if (as === "a" && href) {
    return (
      <a href={href} data-testid={testid} style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        {children}
      </a>
    );
  }
  return (
    <button
      type={type} onClick={onClick} disabled={disabled} data-testid={testid}
      style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, full = false, testid, as, to, href }) {
  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "12px 22px", background: "#fff", color: INK,
    fontWeight: 600, fontSize: 14, borderRadius: 8, border: `1px solid ${BORDER}`,
    cursor: "pointer", width: full ? "100%" : "auto",
    transition: "border-color 120ms ease", textDecoration: "none",
  };
  const hoverIn = (e) => (e.currentTarget.style.borderColor = INK);
  const hoverOut = (e) => (e.currentTarget.style.borderColor = BORDER);
  if (as === "link" && to) {
    return (
      <Link to={to} data-testid={testid} style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        {children}
      </Link>
    );
  }
  if (as === "a" && href) {
    return (
      <a href={href} data-testid={testid} style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} style={base} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
      {children}
    </button>
  );
}

export function FieldLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: INK, marginBottom: 6 }}>
      {children}
    </label>
  );
}

export function FieldInput({ icon, trailing, testid, as = "input", ...rest }) {
  const Cmp = as;
  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", gap: 8,
        padding: "10px 12px", border: `1px solid ${BORDER}`, borderRadius: 8, background: "#fff",
        transition: "border-color 120ms ease",
      }}
    >
      {icon && <div style={{ paddingTop: 3 }}>{icon}</div>}
      <Cmp
        {...rest}
        data-testid={testid}
        style={{
          flex: 1, border: "none", outline: "none", fontSize: 14,
          color: INK, background: "transparent", resize: "vertical",
          fontFamily: "inherit",
        }}
      />
      {trailing}
    </div>
  );
}

/* ---------- shared marketing header (navigation per Website Blueprint) ---------- */
const NAV = [
  { label: "Products", href: "/#products" },
  { label: "Integrations", href: "/#integrations" },
  { label: "Solutions", href: "/#solutions" },
  { label: "AI Platform", href: "/#ai" },
  { label: "Pricing", to: "/pricing" },
  { label: "Resources", href: "/#resources" },
  { label: "Customers", href: "/#customers" },
  { label: "Company", href: "/#company" },
];

export function MarketingHeader() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="marketing-header"
      style={{
        position: "sticky", top: 0, zIndex: 40, background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(12px)", borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div
        style={{
          maxWidth: 1440, margin: "0 auto", padding: "14px 40px",
          display: "flex", alignItems: "center", gap: 32,
        }}
      >
        <Link to="/" style={{ textDecoration: "none", display: "block" }} data-testid="header-logo-link">
          <img
            src={ZAROORI_JINI_LOGO_URL}
            alt="Zaroori Jini OMS · Powered by Zaroori Retail"
            style={{ height: 48, width: "auto", objectFit: "contain", display: "block" }}
            data-testid="header-oms-logo"
          />
        </Link>

        <nav
          className="marketing-nav-desktop"
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 22 }}
        >
          {NAV.map((n) =>
            n.to ? (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{
                  fontSize: 14, fontWeight: 600,
                  color: pathname === n.to ? RED : INK,
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = RED)}
                onMouseLeave={(e) => (e.currentTarget.style.color = pathname === n.to ? RED : INK)}
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.label}
                href={n.href}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ fontSize: 14, fontWeight: 600, color: INK, textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = RED)}
                onMouseLeave={(e) => (e.currentTarget.style.color = INK)}
              >
                {n.label}
              </a>
            )
          )}
          <PrimaryButton as="link" to="/contact-sales" testid="nav-contact-sales">
            Contact Sales
          </PrimaryButton>
        </nav>

        <button
          type="button"
          className="marketing-nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-nav-toggle"
          aria-label="Toggle navigation"
          style={{
            marginLeft: "auto", background: "transparent", border: "none",
            fontSize: 22, color: INK, cursor: "pointer", display: "none",
          }}
        >
          ☰
        </button>
      </div>

      {open && (
        <div
          className="marketing-nav-mobile-panel"
          data-testid="mobile-nav-panel"
          style={{
            display: "none", padding: "12px 24px 20px", background: "#fff",
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          {NAV.map((n) =>
            n.to ? (
              <Link
                key={n.label}
                to={n.to}
                onClick={() => setOpen(false)}
                style={{
                  display: "block", padding: "10px 0",
                  fontSize: 15, fontWeight: 600, color: INK, textDecoration: "none",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                style={{
                  display: "block", padding: "10px 0",
                  fontSize: 15, fontWeight: 600, color: INK, textDecoration: "none",
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                {n.label}
              </a>
            )
          )}
          <Link
            to="/contact-sales"
            onClick={() => setOpen(false)}
            style={{
              display: "block", marginTop: 14, padding: "12px 16px",
              background: RED, color: "#fff", borderRadius: 8,
              textAlign: "center", textDecoration: "none", fontWeight: 700,
            }}
          >
            Contact Sales
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 1080px) {
          .marketing-nav-desktop { display: none !important; }
          .marketing-nav-mobile-toggle { display: block !important; }
          .marketing-nav-mobile-panel { display: block !important; }
        }
      `}</style>
    </header>
  );
}

/* ---------- shared marketing footer (expanded enterprise footer) ---------- */
const FOOTER_COLS = [
  {
    title: "Products",
    items: [
      ["AI Order Management", "/#products"],
      ["Inventory", "/#products"],
      ["Warehouse (WMS)", "/#products"],
      ["Shipping & Couriers", "/#products"],
      ["Returns & Exchange", "/#products"],
      ["Finance & Reconciliation", "/#products"],
      ["AI Command Center", "/#ai"],
      ["Developer APIs", "/#developers"],
    ],
  },
  {
    title: "Integrations",
    items: [
      ["Marketplaces", "/#integrations"],
      ["Quick Commerce", "/#integrations"],
      ["Websites & D2C", "/#integrations"],
      ["Couriers", "/#integrations"],
      ["ERPs & Accounting", "/#integrations"],
      ["Payments", "/#integrations"],
    ],
  },
  {
    title: "Solutions",
    items: [
      ["Fashion & Lifestyle", "/#solutions"],
      ["Beauty & Cosmetics", "/#solutions"],
      ["Jewellery & Footwear", "/#solutions"],
      ["Electronics & FMCG", "/#solutions"],
      ["Manufacturers", "/#solutions"],
      ["Agencies", "/#solutions"],
      ["Enterprise", "/contact-sales"],
    ],
  },
  {
    title: "Company",
    items: [
      ["About Zaroori Retail", "/#company"],
      ["Customers", "/#customers"],
      ["Resources", "/#resources"],
      ["Book a Demo", "/demo"],
      ["Contact Sales", "/contact-sales"],
    ],
  },
  {
    title: "Legal & Trust",
    items: [
      ["Privacy Policy", "/privacy"],
      ["Terms of Service", "/terms"],
      ["Security", "/security"],
      ["Pricing", "/pricing"],
    ],
  },
];

export function MarketingFooter() {
  return (
    <footer
      data-testid="footer"
      style={{ background: "#0F172A", color: "#fff", padding: "64px 24px 24px" }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div
          className="footer-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr repeat(5, 1fr)",
            gap: 32,
          }}
        >
          <div>
            <div
              style={{
                display: "inline-block", background: "#fff", padding: "10px 18px",
                borderRadius: 10, boxShadow: "0 0 0 1px rgba(255,255,255,0.06)",
              }}
            >
              <img
                src={ZAROORI_JINI_LOGO_URL}
                alt="Zaroori Jini OMS"
                style={{ height: 40, width: "auto", objectFit: "contain", display: "block" }}
                data-testid="footer-oms-logo"
              />
            </div>
            <p style={{ fontSize: 13, color: "#CBD5E1", marginTop: 16, maxWidth: 340, lineHeight: 1.6 }}>
              AI-native, API-first, multi-tenant Order Management System unifying
              orders, inventory, warehouses, shipping, marketplaces, finance,
              and automation on a single platform.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              {["Made in Bharat", "SOC-ready", "99.9% uptime"].map((b) => (
                <span
                  key={b}
                  style={{
                    fontSize: 11, fontWeight: 600, color: "#F87171",
                    background: "rgba(220,38,38,0.12)", padding: "4px 10px",
                    borderRadius: 999, border: "1px solid rgba(248,113,113,0.3)",
                  }}
                >
                  {b}
                </span>
              ))}
            </div>
          </div>

          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "#F87171",
                }}
              >
                {col.title}
              </div>
              <ul style={{ listStyle: "none", padding: 0, marginTop: 14 }}>
                {col.items.map(([label, to]) => (
                  <li key={label} style={{ padding: "5px 0" }}>
                    {to.startsWith("/") && !to.includes("#") ? (
                      <Link
                        to={to}
                        style={{ color: "#CBD5E1", textDecoration: "none", fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#CBD5E1")}
                      >
                        {label}
                      </Link>
                    ) : (
                      <a
                        href={to}
                        style={{ color: "#CBD5E1", textDecoration: "none", fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#CBD5E1")}
                      >
                        {label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 44, paddingTop: 20,
            display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
            fontSize: 12, color: "#94A3B8",
          }}
        >
          <div>© {new Date().getFullYear()} Zaroori Retail. All Rights Reserved.</div>
          <div>
            <a href="mailto:hello@zaroorijini.com" style={{ color: "#94A3B8", textDecoration: "none" }}>
              hello@zaroorijini.com
            </a>
            {" · "}Made in Bharat · भारत
          </div>
        </div>
      </div>
      <style>{`
        @media (max-width: 1080px) {
          .footer-grid { grid-template-columns: 1fr 1fr 1fr !important; }
        }
        @media (max-width: 640px) {
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
    </footer>
  );
}

/* ---------- shared page shell ---------- */
export function MarketingShell({ children, testid = "marketing-page" }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "#fff",
        color: INK,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
        minHeight: "100vh",
      }}
    >
      <MarketingHeader />
      {children}
      <MarketingFooter />
    </div>
  );
}
